// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xHeaderUpdate from "@ces/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@ces/App/Hooks/useSize";
import { CustomAppState } from "@ces/App/Store/Reducers";
import {
    getAddressLocationsWidget,
    getCartLinkWidget,
    getHeaderActionsWidget,
    getHeaderLinkListWidget,
    getHeaderSearchWidget,
    getHeaderSignInWidget,
    getHeaderSpotZone,
    getJobTrackWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
    getSecondaryNavigationWidget,
} from "@ces/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@ces/XDL/constants";
import Menu from "@ces/XDL/Icons/BarsMenu";
import Close from "@ces/XDL/Icons/Close";
import { color, layoutContainer, xClassNames, xParserOptions, xScreen } from "@ces/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    // XNG-Changes
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const checkoutReviewAndSubmitPageUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
    const referredFromCheckout = returnUrl === shippingPageUrl || returnUrl === checkoutReviewAndSubmitPageUrl;

    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerActionsWidget: getHeaderActionsWidget(state),
        secondaryNavigationWidget: getSecondaryNavigationWidget(state),
        headerLinkListWidget: getHeaderLinkListWidget(state),
        headerSearchWidget: getHeaderSearchWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        // XNG-Changes
        headerSpotZone: getHeaderSpotZone(state),
        addressLocationsWidget: getAddressLocationsWidget(state),
        jobTrackWidget: getJobTrackWidget(state),
        isCheckoutPage:
            checkoutPages.includes(getCurrentPage(state).type) ||
            getCurrentPage(state).type === "RfqCustomRequestQuotePage" ||
            (referredFromCheckout && getCurrentPage(state).type === "SignInPage"),
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    navigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    // XNG-CHANGES
    wrapperStyledWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    itemsWrapper?: InjectableCss;
    headerLinkListWrapper?: InjectableCss;
    mainNavigationPageWrapper?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    closeMenuClickable?: ClickableProps;
    spotStyle?: InjectableCss;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 50%;
            z-index: 1;
            opacity: 0;
            transform: translateY(-100%) translateX(-50%);
            padding: ${props => props.theme.header.padding};
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            align-self: center;
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        logo: {
            wrapper: {
                css: css`
                    width: 108px;
                    height: 40px;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                `,
            },
        },
    } as Styles);

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean; isCheckoutPage?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    ${(props: any) => {
        if (props.isEdit) {
            return "padding: 30px 10px;";
        }
        return "padding: 8px 16px;";
    }}
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    & .logo-and-search-wrapper {
        display: flex;
        align-items: center;
        flex-grow: 1;
        gap: 54px;
    }
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef);
    // </XNG-Changes>

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            if (scrollPos > Number(props.theme.header.triggerPoint)) {
                setIsShow(true);
                isSticky = true;
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    useEffect(() => {
        if (headerHeight?.height) {
            props.xHeaderUpdate({ key: "stickyHeader", value: `${headerHeight.height}` });
        }
    }, [headerHeight?.height]);

    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    if (isTabletScreen) {
        return null;
    }
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <Transition in={isShow} timeout={0}>
            {state => (
                <Page
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 200ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                    {...{ showMainNavigation }}
                >
                    <StyledWrapper ref={headerRef}>
                        <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                            <InnerWrapper
                                {...styles.pageInnerWrapper}
                                isEdit={isEdit}
                                isCheckoutPage={props.isCheckoutPage}
                            >
                                <StyledWrapper className="logo-and-search-wrapper" {...styles.logoLinksSearchWrapper}>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        {!props.isCheckoutPage && (
                                            <Clickable
                                                onClick={openNavigationDrawer}
                                                data-test-selector="expandMobileMenu"
                                                aria-label="main-navigation"
                                                {...styles.closeMenuClickable}
                                            >
                                                <Icon
                                                    src={showMainNavigation ? Close : Menu}
                                                    size={showMainNavigation ? 14 : 18}
                                                    color="text.link"
                                                />
                                            </Clickable>
                                        )}
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: props.logoImageWidget?.logoImage,
                                                logoImage: props.logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>

                                    {!props.isCheckoutPage && (
                                        <StyledWrapper {...styles.searchWrapper}>
                                            {createWidgetElement("Header/HeaderSearchInput", {
                                                fields: {
                                                    ...props.headerSearchWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {!props.isCheckoutPage && (
                                    <StyledWrapper {...styles.rightSectionWrapper}>
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement("Common/HeaderSignIn", {
                                                fields: {
                                                    ...props.headerSignInWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement("Common/CartLink", {
                                                fields: {
                                                    ...props.cartLinkWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                {props.isCheckoutPage && !!props?.headerSpotZone?.content && (
                                    <StyledWrapper {...styles.rightSectionWrapper}>
                                        <StyledWrapper {...styles.spotStyle}>
                                            {parse(props.headerSpotZone.content, xParserOptions)}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </InnerWrapper>
                            {!props.isCheckoutPage ? (
                                <StyledWrapper {...{ showMainNavigation }} {...styles.navigationWrapper}>
                                    <Page {...styles.mainNavigationPageWrapper}>
                                        <StyledWrapper {...styles.mainNavigationWrapper}>
                                            {createWidgetElement("Header/MainNavigation", {
                                                fields: { ...props.mainNavigationWidget },
                                            })}
                                        </StyledWrapper>
                                        {/* XNG-Changes */}
                                        <StyledWrapper {...styles.headerLinkListWrapper}>
                                            {props.jobTrackWidget &&
                                                createWidgetElement("Header/JobTrack", {
                                                    fields: {
                                                        ...props.jobTrackWidget,
                                                    },
                                                })}
                                            {props.addressLocationsWidget &&
                                                createWidgetElement("Header/AddressLocations", {
                                                    fields: {},
                                                })}
                                        </StyledWrapper>
                                    </Page>
                                </StyledWrapper>
                            ) : (
                                <></>
                            )}
                        </StylesProvider>
                    </StyledWrapper>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
